import { createSelector } from 'reselect';
import { createReducer } from '../reducers/factory';
import friendsApi from '../api/radarFriends';
import { unreadMessagesSelector } from './cometChat';
import { LOGOUT_SUCCESSFUL } from '../reducers/auth';
import { plainFetchSelectorCreator } from '../utils/selectors';
import { CLEAR_RADAR_USER_DATA } from './radarUserProfile';

const reducerName = 'radarFriends';

const fetchReducer = createReducer('FRIENDS', {
  fetchTimeout: 30 * 1000,
  invalidateActions: ['LOGIN_SUCCESSFUL'],
  minUpdate: 15 * 60 * 1000,
  initialState: {
    items: [],
  },
});

const ADD_FRIEND = 'ADD_FRIEND';
const BLOCK_FRIENDS = 'BLOCK_FRIENDS';
const UNBLOCK_FRIENDS = 'UNBLOCK_FRIENDS';
const DELETE_FRIEND = 'DELETE_FRIEND';
const CLEAR_FRIEND_SEARCH = 'CLEAR_FRIEND_SEARCH';
const SET_SEARCHED_FRIEND = 'SET_SEARCHED_FRIEND';
const ACKNOWLEDGE_FRIENDS_ENTRY = 'ACKNOWLEDGE_FRIENDS_ENTRY';

const FriendsReducer = (state = fetchReducer.INITIAL_STATE, action = {}) => {
  switch (action.type) {
    case ADD_FRIEND:
      return {
        ...state,
        items: [...state.items, action.friend],
      };
    case BLOCK_FRIENDS:
      return {
        ...state,
        items: state.items.map((item) =>
          action.id === item.id ? { ...item, status: 'blocked' } : item,
        ),
      };
    case UNBLOCK_FRIENDS:
      return {
        ...state,
        items: state.items.map((item) =>
          action.id === item.id ? { ...item, status: 'friend' } : item,
        ),
      };
    case DELETE_FRIEND: {
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.id),
      };
    }
    case ACKNOWLEDGE_FRIENDS_ENTRY: {
      return {
        ...state,
        items: state.items.map((item) =>
          action.friends.includes(item.id)
            ? { ...item, acknowledged: true }
            : item,
        ),
      };
    }
    case LOGOUT_SUCCESSFUL:
    case CLEAR_RADAR_USER_DATA:
      return { ...fetchReducer.INITIAL_STATE };
    default:
      return fetchReducer(state, action);
  }
};

export const friendsPlainSelector = plainFetchSelectorCreator(reducerName);
export const friendsSelector = ({ [reducerName]: { items } }) => items || [];
export const invitationsSelector = ({ radarInvitations: { items } }) =>
  items || [];

const notAcknowledgedEntriesSelector = createSelector(
  friendsSelector,
  invitationsSelector,
  (friends, invites) => ({
    friends: friends
      .filter((item) => !item.acknowledged)
      .map((item) => item.id),
    invites: invites
      .filter((item) => !item.acknowledged)
      .map((item) => item.id),
  }),
);

export const radarFriendsIndicatorSelector = createSelector(
  notAcknowledgedEntriesSelector,
  ({ friends, invites }) => friends.length + invites.length,
);

export const radarIndicatorCountSelector = createSelector(
  radarFriendsIndicatorSelector,
  unreadMessagesSelector,
  (unacknowledgedRadarFriendsCount, unreadMessagesCount) =>
    unacknowledgedRadarFriendsCount + unreadMessagesCount,
);

export const addFriend = (friend) => ({ type: ADD_FRIEND, friend });
const blockFriends = (id) => ({ type: BLOCK_FRIENDS, id });
const unblockFriends = (id) => ({ type: UNBLOCK_FRIENDS, id });
export const removeFriend = (id) => ({ type: DELETE_FRIEND, id });
const clearFriendSearch = () => ({ type: CLEAR_FRIEND_SEARCH });
const setSearchedFriend = (uid, friend) => ({
  type: SET_SEARCHED_FRIEND,
  uid,
  friend,
});
const acknowledgeFriends = (friends, invites) => ({
  type: ACKNOWLEDGE_FRIENDS_ENTRY,
  friends,
  invites,
});

const fetchFriendsAction = () => async (dispatch, getState) => {
  const {
    auth: { token },
    radarInvitations: { searchedFriend },
  } = getState();

  if (!token) return [];

  const fetchPromise = (async () => {
    try {
      let availableFriend = null;
      const fetchedFriends = await friendsApi.fetchFriends(token);

      const searchedCuid =
        searchedFriend?.cuid || // stranger or friend
        searchedFriend?.user?.cuid || // invitation (to me)
        searchedFriend?.invitee?.cuid; // invitation (from me)

      // searched user changed to friend
      if (searchedCuid) {
        availableFriend = fetchedFriends.find(
          (el) => el?.cuid === searchedCuid,
        );
      }

      dispatch(fetchReducer.fetchSuccessful(fetchedFriends));

      // clear if searched user available in the list or was available before fetching
      if (availableFriend || searchedFriend?.duplicate) {
        dispatch(setSearchedFriend('', null));
      }
    } catch (error) {
      dispatch(fetchReducer.fetchFailed(error));
    }
  })();

  dispatch(fetchReducer.fetchInProgress(fetchPromise));
  return fetchPromise;
};

export const fetchFriendsIfNeeded =
  (forceUpdate) => async (dispatch, getState) => {
    const {
      [reducerName]: { fetchPromise, items, ...state },
    } = getState();

    if (forceUpdate || fetchReducer.shouldFetch(state)) {
      return dispatch(fetchFriendsAction());
    }

    return fetchPromise?.then === 'function' ? fetchPromise : items;
  };

export const blockFriend = (id) => async (dispatch, getState) => {
  const {
    auth: { token },
    radarInvitations: { searchedFriend, searchedFriendCode },
  } = getState();

  if (!id || !token) {
    return null;
  }

  await friendsApi.blockFriend(token, id);

  // set friends statuses to 'blocked'
  dispatch(blockFriends(id));
  if (id === searchedFriend?.id) {
    // update searched friend too
    dispatch(
      setSearchedFriend(searchedFriendCode, {
        ...searchedFriend,
        status: 'blocked',
      }),
    );
  }

  return null;
};

export const unblockFriend = (id) => async (dispatch, getState) => {
  const {
    auth: { token },
    radarInvitations: { searchedFriend, searchedFriendCode },
  } = getState();

  if (!id || !token) {
    return null;
  }

  // todo: handle errors somehow
  await friendsApi.unblockFriend(token, id);

  // set friends statuses to 'FRIEND'
  dispatch(unblockFriends(id));
  if (id === searchedFriend?.id) {
    dispatch(
      setSearchedFriend(searchedFriendCode, {
        ...searchedFriend,
        status: 'blocked',
      }),
    );
  }

  return null;
};

export const deleteFriend = (friendId) => async (dispatch, getState) => {
  const {
    auth: { token },
    radarInvitations: { searchedFriend },
  } = getState();

  if (!token || !friendId) {
    return null;
  }

  // todo: handle somehow errors
  await friendsApi.deleteFriend(token, friendId);

  dispatch(removeFriend(friendId));
  if (friendId === searchedFriend?.id) {
    dispatch(clearFriendSearch());
  }
  return null;
};

export const fetchPlayerDetails =
  (id, type = 'friendcode') =>
  (dispatch, getState) => {
    const {
      auth: { token },
    } = getState();

    if (!token || !id) {
      return null;
    }

    // todo: handle it somehow in case of error
    return friendsApi.fetchPlayer(token, id, type);
  };

export const acknowledgeEntry =
  (friends, invites) => async (dispatch, getState) => {
    const {
      auth: { token },
    } = getState();

    if (!token || (friends.length === 0 && invites.length === 0)) {
      return;
    }

    try {
      await friendsApi.acknowledgeEntry(token, friends, invites);
      dispatch(acknowledgeFriends(friends, invites));
    } catch (e) {
      // ignore errors
    }
  };

export const acknowledgeAll = () => async (dispatch, getState) => {
  const { friends, invites } = notAcknowledgedEntriesSelector(getState());
  dispatch(acknowledgeEntry(friends, invites));
};

export default { [reducerName]: FriendsReducer };
